// WP-2.8 (CA-6, CN-2, OB-1, S-39..46 "AI"): budget cap + this-month meter, tier settings
// read-mostly, STT language preference (English/Urdu/mirror), a basic activity-log viewer,
// and (WP-C1.6, OD-3/ST-3) the reply-language override — a SEPARATE setting from STT language:
// one controls what language captures are transcribed FROM, the other what language the
// assistant's own replies come back IN. The cap and both languages are plain settings keys,
// read/written through the existing generic settings routes (WP-1.19) — this page adds no new
// write endpoint, only the read-only /api/v1/ai-usage aggregation (WP-2.8's own new route).
import { formatMoney } from '@pfos/engine';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Field, inputClass, primaryButtonInlineClass, selectClass } from '../../components/FormKit';
import { useAiUsage } from '../../lib/aiUsage';
import { useSettings, useSetSetting } from '../../lib/settings';

function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

const LANGUAGE_OPTIONS: { value: 'en' | 'ur' | 'auto'; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'ur', label: 'Urdu' },
  { value: 'auto', label: 'Mirror (auto-detect)' },
];

export function SettingsAi(): React.JSX.Element {
  const { data: settings } = useSettings();
  const { data: usage, isLoading: usageLoading } = useAiUsage();
  const setSetting = useSetSetting();

  const [capDollars, setCapDollars] = useState('');
  const [language, setLanguage] = useState<'en' | 'ur' | 'auto'>('auto');
  const [replyLanguage, setReplyLanguage] = useState<'en' | 'ur' | 'auto'>('auto');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (usage !== undefined) setCapDollars((usage.capCents / 100).toFixed(2));
  }, [usage]);

  useEffect(() => {
    const raw = asString(settings?.sttLanguage, 'auto');
    setLanguage(raw === 'en' || raw === 'ur' ? raw : 'auto');
  }, [settings]);

  useEffect(() => {
    // OD-3/ST-3: independent from the STT language above — this pins/mirrors the language of
    // the assistant's OWN replies, not what language voice/text captures get transcribed in.
    const raw = asString(settings?.replyLanguage, 'auto');
    setReplyLanguage(raw === 'en' || raw === 'ur' ? raw : 'auto');
  }, [settings]);

  const spentCents = usage?.spentCents ?? 0;
  const capCents = usage?.capCents ?? 1;
  const percent = Math.min(100, Math.round((spentCents / capCents) * 100));
  const overCap = spentCents >= capCents;

  const save = (): void => {
    setSaved(false);
    const dollars = Number(capDollars);
    if (Number.isFinite(dollars) && dollars > 0) {
      setSetting.mutate({ key: 'aiMonthlyCapCents', value: Math.round(dollars * 100) });
    }
    setSetting.mutate({ key: 'sttLanguage', value: language });
    setSetting.mutate({ key: 'replyLanguage', value: replyLanguage });
    setSaved(true);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-4 text-sm font-medium text-ink">Budget (CA-6)</h2>

        {usageLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : (
          <div className="mb-4">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="text-ink">
                {formatMoney({ currency: 'USD', minorUnits: spentCents })} of{' '}
                {formatMoney({ currency: 'USD', minorUnits: capCents })} this month
              </span>
              <span className={overCap ? 'font-medium text-danger' : 'text-muted'}>
                {String(percent)}%
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-bg">
              <div
                className={`h-full rounded-full ${overCap ? 'bg-danger' : 'bg-primary'}`}
                style={{ width: `${String(percent)}%` }}
              />
            </div>
            {overCap && (
              <p className="mt-2 text-xs text-danger">
                Cap reached — new voice/text captures the pattern parser can't handle land in the
                Inbox for manual review instead of calling the AI (CA-6, never fails silently).
              </p>
            )}
          </div>
        )}

        <Field label="Monthly cap (USD)" htmlFor="ai-cap">
          <input
            id="ai-cap"
            className={inputClass}
            inputMode="decimal"
            placeholder="10.00"
            value={capDollars}
            onChange={(e) => {
              setCapDollars(e.target.value);
            }}
          />
        </Field>

        <Field label="Voice/text language (mirrors your message unless set)" htmlFor="ai-language">
          <select
            id="ai-language"
            className={selectClass}
            value={language}
            onChange={(e) => {
              setLanguage(e.target.value as 'en' | 'ur' | 'auto');
            }}
          >
            {LANGUAGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Reply language (mirrors your message unless set)"
          htmlFor="ai-reply-language"
        >
          <select
            id="ai-reply-language"
            className={selectClass}
            value={replyLanguage}
            onChange={(e) => {
              setReplyLanguage(e.target.value as 'en' | 'ur' | 'auto');
            }}
          >
            {LANGUAGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>

        <button
          type="button"
          className={primaryButtonInlineClass}
          disabled={setSetting.isPending}
          onClick={save}
        >
          Save
        </button>
        {saved && <span className="ml-2 text-sm text-success">Saved</span>}
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-2 text-sm font-medium text-ink">Pipeline tiers</h2>
        <ul className="space-y-1 text-sm text-muted">
          <li>
            <span className="font-medium text-ink">Tier 0</span> — pattern parser, zero API cost.
            Handles "800 lunch cash"- style captures with no AI call at all (CA-1).
          </li>
          <li>
            <span className="font-medium text-ink">Tier 1</span> — a cheap LLM call for anything
            Tier 0 can't parse (CA-2). Counted against the cap above.
          </li>
          <li>
            <span className="font-medium text-ink">Tier 2</span> — an optional stronger-model
            re-pass (CA-3), only when Tier 1 comes back low-confidence or finds 3+ linked events.
            Off unless a Tier-2 model is configured server-side.
          </li>
        </ul>
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-medium text-ink">Activity log</h2>
          <Link
            to="/settings/ai/activity"
            className="text-sm font-medium text-primary hover:underline"
          >
            View full log (OB-1) →
          </Link>
        </div>
        {usage === undefined || usage.recent.length === 0 ? (
          <p className="text-sm text-muted">No AI requests recorded yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {usage.recent.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-ink">
                  {r.kind === 'stt' ? 'Voice transcription' : 'Text extraction'} · {r.provider}
                </span>
                <span className="text-muted">
                  {formatMoney({ currency: 'USD', minorUnits: r.costCents })} ·{' '}
                  {new Date(r.at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
